/**
 * Content Type Detector Utility
 *
 * Detects whether content is HTML or Markdown based on syntax patterns.
 * Used when backend doesn't explicitly indicate content type.
 */
export class ContentTypeDetector {

    /**
     * Detects if content is Markdown or HTML
     * @param content The content string to analyze
     * @returns 'markdown' | 'html'
     */
    static detectContentType(content: string | null | undefined): 'markdown' | 'html' {
        if (!content || content.trim() === '') {
            return 'html'; // Default to HTML for empty content
        }

        const trimmedContent = content.trim();

        // Calculate scores for markdown and HTML patterns
        const markdownScore = this.calculateMarkdownScore(trimmedContent);
        const htmlScore = this.calculateHtmlScore(trimmedContent);

        // If HTML score is significantly higher, it's HTML
        // Otherwise, if we detect any markdown patterns, treat as markdown
        if (htmlScore > markdownScore * 2) {
            return 'html';
        }

        return markdownScore > 0 ? 'markdown' : 'html';
    }

    /**
     * Calculate a score based on markdown syntax patterns
     */
    private static calculateMarkdownScore(content: string): number {
        let score = 0;

        // Markdown headers (# ## ### etc)
        const headerPattern = /^#{1,6}\s+.+$/gm;
        const headers = content.match(headerPattern);
        if (headers) {
            score += headers.length * 3;
        }

        // Bold/Italic patterns (**text** or *text*)
        const boldPattern = /\*\*[^*]+\*\*/g;
        const italicPattern = /\*[^*]+\*/g;
        const bolds = content.match(boldPattern);
        const italics = content.match(italicPattern);
        if (bolds) score += bolds.length * 2;
        if (italics) score += italics.length * 1;

        // Links [text](url)
        const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
        const links = content.match(linkPattern);
        if (links) {
            score += links.length * 3;
        }

        // Code blocks ```
        const codeBlockPattern = /```[\s\S]*?```/g;
        const codeBlocks = content.match(codeBlockPattern);
        if (codeBlocks) {
            score += codeBlocks.length * 4;
        }

        // Inline code `code`
        const inlineCodePattern = /`[^`]+`/g;
        const inlineCodes = content.match(inlineCodePattern);
        if (inlineCodes) {
            score += inlineCodes.length * 1;
        }

        // Lists (- item or * item or 1. item)
        const listPattern = /^[\s]*[-*+]\s+.+$/gm;
        const orderedListPattern = /^[\s]*\d+\.\s+.+$/gm;
        const lists = content.match(listPattern);
        const orderedLists = content.match(orderedListPattern);
        if (lists) score += lists.length * 2;
        if (orderedLists) score += orderedLists.length * 2;

        // Blockquotes (> text)
        const blockquotePattern = /^>\s+.+$/gm;
        const blockquotes = content.match(blockquotePattern);
        if (blockquotes) {
            score += blockquotes.length * 2;
        }

        // Horizontal rules (--- or ***)
        const hrPattern = /^[\s]*[-*_]{3,}[\s]*$/gm;
        const hrs = content.match(hrPattern);
        if (hrs) {
            score += hrs.length * 2;
        }

        return score;
    }

    /**
     * Calculate a score based on HTML tag patterns
     */
    private static calculateHtmlScore(content: string): number {
        let score = 0;

        // Common HTML tags
        const htmlTags = [
            /<p[^>]*>[\s\S]*?<\/p>/gi,
            /<div[^>]*>[\s\S]*?<\/div>/gi,
            /<span[^>]*>[\s\S]*?<\/span>/gi,
            /<h[1-6][^>]*>[\s\S]*?<\/h[1-6]>/gi,
            /<ul[^>]*>[\s\S]*?<\/ul>/gi,
            /<ol[^>]*>[\s\S]*?<\/ol>/gi,
            /<li[^>]*>[\s\S]*?<\/li>/gi,
            /<a[^>]*>[\s\S]*?<\/a>/gi,
            /<strong[^>]*>[\s\S]*?<\/strong>/gi,
            /<em[^>]*>[\s\S]*?<\/em>/gi,
            /<b[^>]*>[\s\S]*?<\/b>/gi,
            /<i[^>]*>[\s\S]*?<\/i>/gi,
            /<br[\s]*\/?>/gi,
            /<img[^>]*>/gi,
            /<table[^>]*>[\s\S]*?<\/table>/gi
        ];

        for (const pattern of htmlTags) {
            const matches = content.match(pattern);
            if (matches) {
                score += matches.length * 3;
            }
        }

        // Check for common Quill editor specific classes
        const quillPatterns = [
            /class="ql-/g,
            /class="mention"/g,
            /data-full-text=/g,
            /data-value=/g
        ];

        for (const pattern of quillPatterns) {
            const matches = content.match(pattern);
            if (matches) {
                score += matches.length * 5; // Higher weight for editor-specific patterns
            }
        }

        return score;
    }

    /**
     * Checks if content contains any HTML tags
     */
    static containsHtmlTags(content: string | null | undefined): boolean {
        if (!content) {
            return false;
        }

        const htmlTagPattern = /<[^>]+>/;
        return htmlTagPattern.test(content);
    }

    /**
     * Checks if content contains markdown syntax
     */
    static containsMarkdownSyntax(content: string | null | undefined): boolean {
        if (!content) {
            return false;
        }

        // Check for common markdown patterns
        const markdownPatterns = [
            /^#{1,6}\s+/gm,           // Headers
            /\*\*[^*]+\*\*/,           // Bold
            /\[([^\]]+)\]\(([^)]+)\)/, // Links
            /```/,                     // Code blocks
            /^[-*+]\s+/gm,            // Unordered lists
            /^\d+\.\s+/gm,            // Ordered lists
            /^>\s+/gm                 // Blockquotes
        ];

        return markdownPatterns.some(pattern => pattern.test(content));
    }
}
